/**
 * Role entity interface
 * Maps to ABP Identity IdentityRoleDto
 */
export interface Role {
  id: string;
  name: string;
  isDefault: boolean;
  isStatic: boolean;
  isPublic: boolean;
  concurrencyStamp: string;
  extraProperties: Record<string, unknown>;
}

/**
 * Extended role with computed fields for UI display
 */
export interface RoleWithMetadata extends Role {
  description?: string;
  permissionCount?: number;
  userCount?: number;
}

/**
 * Create role request DTO
 */
export interface CreateRoleRequest {
  name: string;
  isDefault?: boolean;
  isPublic?: boolean;
  extraProperties?: {
    description?: string;
    [key: string]: unknown;
  };
}

/**
 * Update role request DTO
 */
export interface UpdateRoleRequest {
  name: string;
  isDefault?: boolean;
  isPublic?: boolean;
  concurrencyStamp: string;
  extraProperties?: Record<string, unknown>;
}

/**
 * Permission definition
 * Maps to ABP PermissionGrantInfoDto
 */
export interface Permission {
  name: string;
  displayName: string;
  description?: string;
  parentName?: string;
  isGranted: boolean;
  allowedProviders: string[];
}

/**
 * Permission group
 * Permissions are grouped by provider/resource
 */
export interface PermissionGroup {
  name: string;
  displayName: string;
  permissions: Permission[];
}

/**
 * Role permissions response
 * Maps to ABP GetPermissionListResultDto
 */
export interface RolePermissionsResponse {
  entityDisplayName: string;
  groups: PermissionGroup[];
}

/**
 * Update permissions request
 * Maps to ABP UpdatePermissionsDto
 */
export interface UpdatePermissionsRequest {
  permissions: {
    name: string;
    isGranted: boolean;
  }[];
}

/**
 * All permissions response (custom endpoint)
 */
export interface AllPermissionsResponse {
  permissions: {
    name: string;
    displayName: string;
    description?: string;
    providerName: string;
    parentName?: string;
    isEnabled: boolean;
  }[];
}

/**
 * Query parameters for role list
 */
export interface RoleQueryParams {
  filter?: string;
  sorting?: string;
  skipCount?: number;
  maxResultCount?: number;
}

/**
 * Permission provider types
 */
export enum PermissionProviderType {
  Role = 'R',
  User = 'U',
  Client = 'C',
}
